import {
  Alert,
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  InputAdornment,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useDocumentTitle } from 'hooks'
import { useContext, useState } from 'react'
import { VariantPickingInfo } from 'models'
import { get_variant_picking_info } from 'api/clothes_service'
import { NotFoundError, UnauthorisedError } from 'errors'
import { UserContext } from 'base'
import SearchIcon from '@mui/icons-material/Search'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'

enum Status {
  NOT_STARTED = 'Please enter an Inventory Item ID or SKU.',
  LOADING = 'Loading...',
  NOT_FOUND = 'No result found.',
  FOUND = 'Found',
}

type SearchResult =
  | { status: Status.NOT_STARTED }
  | { status: Status.LOADING }
  | { status: Status.NOT_FOUND }
  | { status: Status.FOUND; variantPickingInfo: VariantPickingInfo }

const InventoryPicking = () => {
  useDocumentTitle('Inventory Picking')
  const user = useContext(UserContext)
  const [inventoryItemIdOrSku, setInventoryItemIdOrSku] = useState<string>('')
  const [searchResult, setSearchResult] = useState<SearchResult>({
    status: Status.NOT_STARTED,
  })

  const isDisabled =
    inventoryItemIdOrSku === '' || searchResult.status === Status.LOADING

  const retrievePickingInfo = async () => {
    setSearchResult({ status: Status.LOADING })
    try {
      const variantPickingInfo =
        await get_variant_picking_info(inventoryItemIdOrSku)
      setSearchResult({
        status: Status.FOUND,
        variantPickingInfo: variantPickingInfo,
      })
    } catch (e: any) {
      if (e instanceof UnauthorisedError) {
        return user.setLoggedOut()
      }
      if (e instanceof NotFoundError) {
        setSearchResult({ status: Status.NOT_FOUND })
      }
    }
  }

  return (
    <Stack spacing={2}>
      <Typography variant="h3">Inventory Picking</Typography>
      <Box
        component="form"
        onSubmit={async (event) => {
          event.preventDefault()
          if (isDisabled) {
            return
          }
          await retrievePickingInfo()
        }}
      >
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <TextField
            required
            fullWidth
            label="Search by Inventory Item ID or SKU"
            value={inventoryItemIdOrSku}
            onChange={(event) => setInventoryItemIdOrSku(event.target.value)}
            size="small"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={retrievePickingInfo}
                    edge="end"
                    aria-label="Search"
                    disabled={isDisabled}
                    color="primary"
                    size="small"
                  >
                    <SearchIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Stack>
      </Box>
      <SearchResultSection searchResult={searchResult} />
    </Stack>
  )
}

interface SearchResultProps {
  searchResult: SearchResult
}

const SearchResultSection = ({ searchResult }: SearchResultProps) => {
  if (searchResult.status === Status.LOADING) {
    return (
      <Stack spacing={1}>
        <Typography variant="h3">
          <Skeleton animation="wave" width="50%" />
        </Typography>
        <Skeleton variant="rounded" animation="wave" width="50%" height={100} />
        <Skeleton animation="wave" width="25%" />
      </Stack>
    )
  }

  if (searchResult.status === Status.NOT_FOUND) {
    return <Alert severity="error">{searchResult.status}</Alert>
  }

  if (searchResult.status !== Status.FOUND) {
    return <Typography>{searchResult.status}</Typography>
  }

  const info = searchResult.variantPickingInfo

  return (
    <Stack alignItems="flex-start" spacing={1}>
      {info.is_only_search_result || (
        <Alert severity="warning" sx={{ width: '100%' }}>
          Note: There are multiple variants with this SKU, but only the first
          one is shown here.
        </Alert>
      )}
      <Typography variant="h4">{info.name}</Typography>
      <Typography variant="body1">
        Inventory item ID: {info.inventory_item_id}
      </Typography>
      <Typography variant="body1">
        SKU:{' '}
        <Box component="span" sx={{ fontWeight: 500 }}>
          {info.sku}
        </Box>
      </Typography>
      <Typography variant="body1">
        Barcode:{' '}
        <Box component="span" sx={{ fontWeight: 500 }}>
          {info.barcode}
        </Box>
      </Typography>
      {info.storage_id && (
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body1">Storage location:</Typography>
          {info.parent_storage_ids.map((storageId) => (
            <Chip label={storageId} variant="outlined" key={storageId} />
          ))}
          <Chip label={info.storage_id} variant="outlined" color="primary" />
        </Stack>
      )}
      {info.clothing_condition && (
        <Typography variant="body1">
          Clothing condition: {info.clothing_condition}
        </Typography>
      )}
      {info.clothing_color_text_list.length > 0 && (
        <Typography variant="body1">
          Clothing colours: {info.clothing_color_text_list.join(', ')}
        </Typography>
      )}
      {info.clothing_pattern.length > 0 && (
        <Typography variant="body1">
          Clothing pattern: {info.clothing_pattern.join(', ')}
        </Typography>
      )}
      {info.book_format && (
        <Typography variant="body1">Format: {info.book_format}</Typography>
      )}
      <Button
        href={info.shopify_variant_link}
        variant="contained"
        target="_blank"
        endIcon={<OpenInNewIcon />}
        style={{ marginBottom: 10 }}
      >
        Shopify Variant
      </Button>
      <Grid container gap={2}>
        {info.image_urls.map((image_url) => (
          <Grid item xs="auto" key={image_url}>
            <ImageWithSkeleton src={image_url} alt={`Image of ${info.name}`} />
          </Grid>
        ))}
      </Grid>
    </Stack>
  )
}

const ImageWithSkeleton = ({ src, alt }: { src: string; alt: string }) => {
  const [isLoaded, setIsLoaded] = useState(false)

  return (
    <div style={{ position: 'relative' }}>
      {!isLoaded && (
        <Skeleton
          variant="rounded"
          animation="wave"
          sx={{
            width: { xs: 150, sm: 200, md: 300 },
            height: { xs: 150, sm: 200, md: 300 },
          }}
        />
      )}
      <Box
        component="img"
        src={src}
        alt={alt}
        onLoad={() => setIsLoaded(true)}
        sx={{
          display: isLoaded ? 'block' : 'none',
          maxWidth: { xs: 150, sm: 200, md: 300 },
          maxHeight: { xs: 150, sm: 200, md: 300 },
        }}
      />
    </div>
  )
}

export default InventoryPicking
